/**
 * HR-portal FTUE — Stakeholder contacts steps (`/stakeholders`).
 *
 * Owns the three on-page coachmarks of the `portal-hr-activation` tour that land on
 * the Stakeholder contacts manager:
 *  - `stakeholdersAdd` spotlights the first section's "Add contact" button and
 *    explains that each stakeholder contact gets a scoped portal login and shows
 *    up in Pelgo's status tracking.
 *  - `stakeholdersEditRow` spotlights an existing contact row's edit/delete icon
 *    pair, once there is at least one contact to show it on.
 *  - `stakeholdersHistory` spotlights the "Removed contacts" header button.
 *
 * Mounted from `StakeholderContactsManager`. The layout hook
 * (`use-portal-hr-ftue`) owns the flow start and the nav step that leads here;
 * this hook only adds the follow-up steps (orders x2-x4) so their `condition`s
 * can close over the page's live state, matching the job-seeker pattern
 * (`use-opportunities-ftue`, `use-learn-ftue`).
 *
 * `stakeholdersAdd`'s anchor is one small "Add contact" `<Button>` that opens the
 * create dialog, so `dismissOnAction: true` — clicking it is intent and completes
 * the step. `stakeholdersEditRow`'s anchor wraps BOTH the edit and delete icon
 * buttons on a row, so it stays a plain "Got it" ack instead — a stray click on
 * either icon should not silently complete the step. `stakeholdersHistory`'s
 * anchor is the single "Removed contacts" button, so it is `dismissOnAction` too.
 *
 * While the contacts list is still loading none of these are rendered, so every
 * step gates on `ready` (the page passes `!isLoading`); `stakeholdersEditRow`
 * additionally gates on `hasContacts` since its anchor row only exists once a
 * contact has been added. The re-eval effect re-picks the candidate once either
 * settles.
 */

import { getStep, registerSteps, useFtueAnchor, useFtueStore } from '@/ftue'
import { useEffect, useRef } from 'react'
import {
  PORTAL_HR_ACTIVATION_FLOW,
  PORTAL_HR_ANCHOR,
  PORTAL_HR_ORDER,
  PORTAL_HR_STEP,
  onPortalPath,
  portalStep,
  reevaluatePortalHrFlow,
} from './flow'

const STAKEHOLDERS_PATH = '/stakeholders'

/** This hook's own step ids, for the re-eval effect below — scoped so it never
 *  retires a step owned by another hook on the shared flow. */
const STAKEHOLDERS_STEP_IDS: readonly string[] = [
  PORTAL_HR_STEP.stakeholdersAdd,
  PORTAL_HR_STEP.stakeholdersEditRow,
  PORTAL_HR_STEP.stakeholdersHistory,
]

export interface PortalStakeholdersFtueArgs {
  /**
   * Whether the contacts list has finished its initial load — the page passes
   * `!isLoading`. The "Add contact" buttons, contact rows, and history button
   * only render once loading clears, so no step here is a candidate before
   * then. Defaults to `true` when omitted.
   */
  ready?: boolean
  /**
   * Whether at least one active contact exists across any department — the page
   * derives this from the contacts list. `stakeholdersEditRow` anchors on an
   * existing contact row, so it is never a candidate until one exists. Defaults
   * to `false` when omitted, so the step stays gated unless the caller opts in.
   */
  hasContacts?: boolean
}

export function usePortalStakeholdersFtue({
  ready = true,
  hasContacts = false,
}: PortalStakeholdersFtueArgs = {}) {
  const addContactAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.stakeholderAddContact)
  const contactRowAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.stakeholderContactRow)
  const historyAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.stakeholderHistoryCta)

  // Live `ready` / `hasContacts` for the step `condition`s (registered once
  // below, so the closures must read the current value, not the mount-time
  // snapshot).
  const readyRef = useRef(ready)
  readyRef.current = ready
  const hasContactsRef = useRef(hasContacts)
  hasContactsRef.current = hasContacts

  useEffect(() => {
    registerSteps([
      portalStep({
        id: PORTAL_HR_STEP.stakeholdersAdd,
        phase: PORTAL_HR_STEP.stakeholdersAdd,
        order: PORTAL_HR_ORDER.stakeholdersAdd,
        trigger: { type: 'immediate' },
        // Not a candidate unless the "Add contact" button is actually rendered: on
        // `/stakeholders`, with the contacts list loaded. Gating on the path as
        // well means this immediate step never blocks the rest of the flow while
        // the admin is on another page (see the note in `flow.ts`).
        condition: () => onPortalPath(STAKEHOLDERS_PATH) && readyRef.current,
        anchorId: PORTAL_HR_ANCHOR.stakeholderAddContact,
        placement: 'bottom',
        // The anchor is the single "Add contact" button; clicking it opens the
        // create dialog (intent), so completing the step there is right.
        dismissOnAction: true,
        content: {
          body: 'Add your IT, Payroll, Legal, Benefits, and Security contacts. Each gets a portal login with only their tasks, and Pelgo tracks their status.',
          dismissLabel: 'Got it',
        },
      }),
      portalStep({
        id: PORTAL_HR_STEP.stakeholdersEditRow,
        phase: PORTAL_HR_STEP.stakeholdersEditRow,
        order: PORTAL_HR_ORDER.stakeholdersEditRow,
        trigger: { type: 'immediate' },
        // Only a candidate once a contact row actually exists to anchor on: on
        // `/stakeholders`, loaded, and at least one active contact. A brand-new org
        // with zero contacts simply never sees this step, same as every other
        // condition-gated step in this flow.
        condition: () =>
          onPortalPath(STAKEHOLDERS_PATH) && readyRef.current && hasContactsRef.current,
        anchorId: PORTAL_HR_ANCHOR.stakeholderContactRow,
        placement: 'bottom',
        // The anchor wraps BOTH the edit and delete icon buttons on the row; a
        // stray click on either is a distinct, consequential action (open the edit
        // dialog vs. delete the contact), so this step is NOT `dismissOnAction` —
        // only the explicit "Got it" ack completes it.
        content: {
          body: 'Edit or remove a contact any time.',
          dismissLabel: 'Got it',
        },
      }),
      portalStep({
        id: PORTAL_HR_STEP.stakeholdersHistory,
        phase: PORTAL_HR_STEP.stakeholdersHistory,
        order: PORTAL_HR_ORDER.stakeholdersHistory,
        trigger: { type: 'immediate' },
        // Always a candidate once loaded and on-page, regardless of contact count
        // — the "Removed contacts" button is in the page header and rendered
        // unconditionally.
        condition: () => onPortalPath(STAKEHOLDERS_PATH) && readyRef.current,
        anchorId: PORTAL_HR_ANCHOR.stakeholderHistoryCta,
        placement: 'bottom',
        // The anchor is the single "Removed contacts" button; clicking it opens
        // the history dialog (intent), so completing the step there is right.
        dismissOnAction: true,
        content: {
          body: 'Removed a contact by mistake? Restore it here.',
          dismissLabel: 'Got it',
        },
      }),
    ])

    // Registration alone does not trigger evaluation — re-pick now the steps
    // exist. Idempotent; the flow is started/owned by `use-portal-hr-ftue`.
    reevaluatePortalHrFlow()
  }, [])

  // Re-evaluate when `ready` or `hasContacts` settle (a plain re-render does not
  // re-run the engine). If our active step's `condition` has just flipped false
  // (the list dropped back to loading, the last contact was removed, or the route
  // changed) and it is not recorded yet, complete it so the tour advances cleanly
  // instead of the engine holding a candidate whose anchor is gone. Scoped to
  // this hook's own step ids because the `portal-hr-activation` flow is shared
  // across every area hook. Otherwise just re-pick the candidate. Mirrors
  // `use-learn-ftue`.
  // biome-ignore lint/correctness/useExhaustiveDependencies: ready/hasContacts intentionally re-trigger evaluation; live state is read via getState()
  useEffect(() => {
    const { activeStepId, seen, complete } = useFtueStore.getState()
    if (activeStepId && STAKEHOLDERS_STEP_IDS.includes(activeStepId) && !seen.has(activeStepId)) {
      const step = getStep(PORTAL_HR_ACTIVATION_FLOW, activeStepId)
      if (step?.condition && !step.condition()) {
        complete(activeStepId)
        return
      }
    }
    reevaluatePortalHrFlow()
  }, [ready, hasContacts])

  return {
    /** Ref callback for the first section's "Add contact" `<Button>`. */
    addContactAnchor,
    /** Ref callback for one existing contact row's edit/delete icon wrapper. */
    contactRowAnchor,
    /** Ref callback for the "Removed contacts" header `<Button>`. */
    historyAnchor,
  }
}
